import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { AuthService } from '../services/auth.service';
import { RETHROW_UNAUTHORIZED } from './http-context-tokens';

@Injectable()
export class UnauthorizedResponseInterceptor implements HttpInterceptor {
  private readonly platformId = inject(PLATFORM_ID);

  constructor(private authService: AuthService, private router: Router) { }

  public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      tap((evt: HttpEvent<any>) => {
        if (evt instanceof HttpErrorResponse) {
          if (evt.status === 401 && !this.isSessionExpiredModalShowing()) {
            this.handleUnauthorized();
          }
        }
      }),
      catchError((err: any) => {
        // Let all errors from maintainer_access propagate so the component can show
        // the correct error message (wrong password = 401, bad program ID = 422, etc.)
        if (req.url.includes('/program_director_rest/maintainer_access')) {
          return throwError(() => err);
        }

        if (err.status === 401 && !this.isSessionExpiredModalShowing()) {
          this.handleUnauthorized();
          // Callers that opt in (e.g. the guards' user lookup) get the 401 as an error instead of
          // an empty completion, so they can settle rather than hang or fall through a route.
          return req.context.get(RETHROW_UNAUTHORIZED) ? throwError(() => err) : of(null);
        }

        // Any other error (network failure, 403, 500, etc.) must propagate rather
        // than resolve to `of(null)` — a non-HttpResponse value here makes the
        // request's observable complete without ever emitting a body event, which
        // breaks any caller composing multiple requests (e.g. combineLatest), since
        // that silently completes empty instead of emitting or erroring. This was
        // surfacing as SpecialtyResolver failing SSR with NoDataFromResolver whenever
        // the (unrelated) field-schema request errored for any non-401 reason.
        return throwError(() => err);
      })
    );
  }

  private handleUnauthorized(): void {
    // sessionStorage and SSO redirects only exist in the browser.
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // staleJwtLoginAttempted acts as a loop-breaker: if we already attempted SSO
    // recovery and still got a 401, don't redirect again — checkIsSignedIn() will
    // show the session expired modal on the next page load.
    if (sessionStorage.getItem('staleJwtLoginAttempted') === 'true') {
      return;
    }

    // Don't trigger SSO in bypass mode (local dev — SSO is unreachable).
    if (environment.bypassAuth) {
      sessionStorage.setItem('showUserLoggedOutModal', 'true');
      this.router.navigate(['/'], { queryParams: { sessionExpired: 'true' } });
      return;
    }

    // If a JWT is present but the server rejected it, clear it before re-authing.
    if (this.authService.getAuthToken() !== '') {
      this.authService.clearSessionCookies();
    }
    sessionStorage.setItem('staleJwtLoginAttempted', 'true');
    this.authService.login();
  }

  private isSessionExpiredModalShowing(): boolean {
    try {
      return typeof sessionStorage !== 'undefined' && sessionStorage.getItem('showUserLoggedOutModal') === 'true';
    } catch {
      return false;
    }
  }
}
